/**
 * De rekensom achter de agenda als kalender (FBK-9): welke dagen een weergave toont, waar je
 * naartoe bladert, en welk stuk van een dag een evenement inneemt.
 *
 * Dagen zijn tekst ("2026-10-28") in de zone van de lezer, zoals `dayOf` uit `@up2d8/ui`: een
 * evenement van 23:30 hoort op de dag waarop de lezer het ziet, niet op die van UTC. Gerekend wordt
 * met `Date.UTC` op die tekst, zodat een zomertijdwissel geen dag laat verdwijnen of dubbel staan.
 */

export type CalendarView = 'day' | 'week' | 'month';

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function parse(day: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) throw new Error(`Geen dag in de vorm JJJJ-MM-DD: ${day}`);
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

function format(date: Date): string {
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

/** Een dag erbij of eraf. */
export function addDays(day: string, delta: number): string {
  const date = parse(day);
  date.setUTCDate(date.getUTCDate() + delta);
  return format(date);
}

/** De maandag van de week waarin deze dag valt. */
export function mondayOf(day: string): string {
  const date = parse(day);
  return addDays(day, -((date.getUTCDay() + 6) % 7));
}

/** De zeven dagen van de week, maandag eerst. */
export function weekDays(day: string): string[] {
  const monday = mondayOf(day);
  return Array.from({ length: 7 }, (_, index) => addDays(monday, index));
}

/** Een stap terug of vooruit in de gekozen weergave: een dag, een week of een maand. */
export function step(view: CalendarView, anchor: string, delta: number): string {
  if (view === 'day') return addDays(anchor, delta);
  if (view === 'week') return addDays(anchor, delta * 7);
  const date = parse(anchor);
  // De eerste van de maand, zodat 31 januari + 1 maand geen 3 maart wordt.
  const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + delta, 1));
  return format(target);
}

/**
 * De dagen die een weergave toont, als [eerste, eerste dag erna). De maand toont het raster van
 * zes weken van `monthGrid`, zodat ook de dagen van de buurmaanden hun evenementen hebben.
 */
export function visibleDays(view: CalendarView, anchor: string): { first: string; after: string } {
  if (view === 'day') return { first: anchor, after: addDays(anchor, 1) };
  if (view === 'week') {
    const monday = mondayOf(anchor);
    return { first: monday, after: addDays(monday, 7) };
  }
  const date = parse(anchor);
  const firstOfMonth = format(new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)));
  const first = mondayOf(firstOfMonth);
  return { first, after: addDays(first, 42) };
}

/**
 * Elke maand ("2026-10") die een reeks dagen [eerste, eerste dag erna) raakt, in volgorde. Het
 * maandraster van zes weken loopt vaak over drie maanden: alleen de eerste en de laatste vragen,
 * liet de lopende maand leeg (testrapport 28 okt., #5).
 */
export function monthsInRange(first: string, after: string): string[] {
  const last = addDays(after, -1).slice(0, 7);
  const months: string[] = [];
  let month = first.slice(0, 7);
  while (month <= last) {
    months.push(month);
    month = step('month', `${month}-01`, 1).slice(0, 7);
  }
  return months;
}

/** Het begin van een dag in de zone van de lezer, als tijdstip. Voor de vraag aan de server. */
export function startOfLocalDay(day: string): Date {
  const date = parse(day);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Het deel van een dag dat een evenement inneemt, in minuten sinds middernacht van die dag,
 * afgekapt op de dag. `null` als het de dag niet raakt.
 */
export function minutesOnDay(
  day: string,
  startsAt: Date,
  endsAt: Date,
): { from: number; to: number } | null {
  const begin = startOfLocalDay(day).getTime();
  const end = startOfLocalDay(addDays(day, 1)).getTime();
  const from = Math.max(startsAt.getTime(), begin);
  const to = Math.min(endsAt.getTime(), end);
  if (to <= from) return null;
  // De lengte van de dag zelf, zodat een dag van 23 of 25 uur (zomertijd) klopt.
  const scale = 1440 / ((end - begin) / 60000);
  return {
    from: Math.round(((from - begin) / 60000) * scale),
    to: Math.round(((to - begin) / 60000) * scale),
  };
}
